import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, useNavigate } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import Select from "react-select";
import TablePagination from "./TablePagination";
import { selectStyles } from "../helper/SelectStyles";
import { Tooltip } from "bootstrap/dist/js/bootstrap.bundle.min.js";
import {
  getClients,
  toggleClientStatus,
  deleteClient,
  downloadSample,
  bulkUpload,
} from "../Apis/ClientAPIs";
import { getLocations } from "../Apis/MasterAPIs";
import { getProducts } from "../Apis/ProductAPIs";
import { server } from "../Apis/api";
import usePermissions from "../hook/userPermission";

const ClientListLayer = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [members, setMembers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);

  const [filterLocation, setFilterLocation] = useState("");
  const [filterSegment, setFilterSegment] = useState("");
  const [filterMobile, setFilterMobile] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isBulkUploading, setIsBulkUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showErrorDownloadModal, setShowErrorDownloadModal] = useState(false);
  const [errorDownloadUrl, setErrorDownloadUrl] = useState("");
  const [errorCount, setErrorCount] = useState(0);

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const clearFilters = () => {
    setSearchTerm("");
    setFilterMobile("");
    setFilterLocation("");
    setFilterSegment("");
    setFilterStatus("");
    setCurrentPage(0);
  };

  const fetchInitialData = async () => {
    setIsLoading(true);
    const [clientRes, locRes, prodRes] = await Promise.all([
      getClients({
        page: currentPage,
        limit: rowsPerPage,
        search: debouncedSearchTerm,
        locationId: filterLocation,
        product: filterSegment,
        mobileNumber: filterMobile,
        isActive: filterStatus,
      }),
      getLocations(),
      getProducts(),
    ]);

    if (clientRes.status) {
      const data = clientRes.response?.data || clientRes.response || [];
      // Filter out clients that belong to any group (they should only list in their group page)
      const ungroupedData = data.filter((member) => !member.groupId);
      setMembers(ungroupedData);
      setTotalRecords(
        clientRes.response?.total || (Array.isArray(ungroupedData) ? ungroupedData.length : 0),
      );
    }
    if (locRes.status) {
      const lData = locRes.response?.data || locRes.response;
      setLocations(Array.isArray(lData) ? lData : []);
    }
    if (prodRes.status) {
      const pData = prodRes.response?.data || prodRes.response;
      setProducts(Array.isArray(pData) ? pData : []);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchInitialData();
  }, [
    currentPage,
    rowsPerPage,
    debouncedSearchTerm,
    filterLocation,
    filterSegment,
    filterMobile,
    filterStatus,
  ]);

  useEffect(() => {
    const tooltipTriggerList = document.querySelectorAll(
      '[data-bs-toggle="tooltip"]',
    );
    const tooltipList = [...tooltipTriggerList].map((tooltipTriggerEl) => {
      return new Tooltip(tooltipTriggerEl, {
        customClass: "theme-tooltip",
      });
    });
    return () => {
      tooltipList.forEach((tooltip) => tooltip.dispose());
    };
  }, [members]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  const locationOptions = [
    { value: "", label: "All Locations" },
    ...locations.map((l) => ({ value: l._id || l.id, label: l.name })),
  ];
  const segmentOptions = [
    { value: "", label: "All Products" },
    ...products.map((p) => ({ value: p._id || p.id, label: p.productName })),
  ];

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value));
    setCurrentPage(0);
  };

  const handleDeleteClick = (member) => {
    setMemberToDelete(member);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (memberToDelete) {
      setIsUpdating(true);
      const res = await deleteClient(memberToDelete._id || memberToDelete.id);
      if (res.status) {
        toast.success("Client Deleted Successfully");
        fetchInitialData();
        setShowDeleteModal(false);
        setMemberToDelete(null);
      }
      setIsUpdating(false);
    }
  };

  const handleCloseDelete = () => {
    setShowDeleteModal(false);
    setMemberToDelete(null);
  };

  const handleStatusToggle = async (id, currentStatus) => {
    setIsUpdating(true);
    const res = await toggleClientStatus(id, { isActive: !currentStatus });
    if (res.status) {
      toast.success("Client Status Updated Successfully");
      fetchInitialData();
    }
    setIsUpdating(false);
  };

  const handleDownloadSample = async () => {
    await downloadSample();
  };

  const handleImportClients = async () => {
    if (!selectedFile) return;

    setIsBulkUploading(true);
    const formData = new FormData();
    formData.append("file", selectedFile);

    const res = await bulkUpload(formData);
    const errUrl = res.response?.data?.errorFileUrl || res.response?.errorFileUrl;
    const errCount = res.response?.data?.errorCount || res.response?.errorCount || 0;

    if (res.status) {
      if (errUrl) {
        setErrorDownloadUrl(errUrl);
        setErrorCount(errCount);
        setShowErrorDownloadModal(true);
      } else {
        toast.success(res.response?.message || "Clients Uploaded Successfully!");
      }
      setShowBulkUploadModal(false);
      setSelectedFile(null);
      fetchInitialData();
    } else {
      if (errUrl) {
        setErrorDownloadUrl(errUrl);
        setErrorCount(errCount);
        setShowErrorDownloadModal(true);
        setShowBulkUploadModal(false);
        setSelectedFile(null);
      }
    }
    setIsBulkUploading(false);
  };

  return (
    <div className="card h-100 p-0 radius-12">
      <div
        className="card-header border-bottom py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between"
        style={{
          background:
            "linear-gradient(135deg, #000000ff, #bb0007ff, #ff0000ff)",
        }}
      >
        <div className="d-flex align-items-center flex-wrap gap-3">
          <h6 className="text-white pb-2 mb-0">Clients</h6>
        </div>
        <div className="d-flex align-items-center flex-wrap gap-2">
          {hasPermission("clients", "add") && (
            <button
              className="btn text-sm btn-sm px-12 py-8 radius-8 d-flex align-items-center gap-2"
              style={{
                backgroundColor: "#f7e7da",
                color: "black",
                fontWeight: "600",
              }}
              onClick={() => setShowBulkUploadModal(true)}
            >
              <Icon
                icon="solar:upload-minimalistic-bold"
                className="icon text-xl line-height-1"
              />
              Bulk Upload
            </button>
          )}

          {hasPermission("clients", "add") && (
            <Link
              to="/clients/add"
              className="btn text-sm btn-sm px-12 py-8 radius-8 d-flex align-items-center gap-2"
              style={{
                backgroundColor: "#f7e7da",
                color: "black",
                fontWeight: "600",
              }}
            >
              <Icon icon="ic:baseline-plus" className="text-xl" />
              Add Client
            </Link>
          )}
        </div>
      </div>
      <div className="card-body p-24">
        {/* Multi-Search Filters */}
        <div className="mb-24 p-16 radius-8 bg-neutral-50 border">
          <div className="row gy-3">
            <div className="col-md-3">
              <label className="form-label text-xs fw-bold text-secondary-light uppercase p-0 m-0 mb-1">
                Search Name/Company
              </label>
              <div className="position-relative">
                <input
                  type="text"
                  className="form-control radius-8 ps-40"
                  style={{ height: "44px" }}
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(0);
                  }}
                />
                <Icon
                  icon="ion:search-outline"
                  className="position-absolute top-50 translate-middle-y text-secondary-light"
                  style={{ fontSize: "20px", left: "14px" }}
                />
              </div>
            </div>
            <div className="col-md-2">
              <label className="form-label text-xs fw-bold text-secondary-light uppercase p-0 m-0 mb-1">
                Mobile Number
              </label>
              <input
                type="text"
                className="form-control radius-8"
                style={{ height: "44px" }}
                placeholder="Enter Mobile"
                value={filterMobile}
                onChange={(e) => {
                  setFilterMobile(e.target.value);
                  setCurrentPage(0);
                }}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label text-xs fw-bold text-secondary-light uppercase p-0 m-0 mb-1">
                Location
              </label>
              <Select
                options={locationOptions}
                isSearchable={false}
                value={locationOptions.find(
                  (opt) => opt.value === filterLocation,
                )}
                onChange={(val) => {
                  setFilterLocation(val ? val.value : "");
                  setCurrentPage(0);
                }}
                styles={selectStyles()}
                placeholder="All Locations"
              />
            </div>
            <div className="col-md-2">
              <label className="form-label text-xs fw-bold text-secondary-light uppercase p-0 m-0 mb-1">
                Product
              </label>
              <Select
                options={segmentOptions}
                isSearchable={false}
                value={segmentOptions.find(
                  (opt) => opt.value === filterSegment,
                )}
                onChange={(val) => {
                  setFilterSegment(val ? val.value : "");
                  setCurrentPage(0);
                }}
                styles={selectStyles()}
                placeholder="All Products"
              />
            </div>
            <div className="col-md-1 d-flex align-items-end">
              <button
                className="btn btn-outline-danger h-44-px w-44-px d-flex align-items-center justify-content-center radius-8 fw-bold p-0"
                onClick={clearFilters}
                title="Clear Filters"
              >
                <Icon icon="solar:restart-bold" className="text-xl" />
              </button>
            </div>
          </div>
        </div>
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr style={{ backgroundColor: "#f7e7da" }}>
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                  S.No
                </th>
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                  Company Name
                </th>
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                  Key Person
                </th>
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                  Mobile Number
                </th>
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                  WhatsApp
                </th>
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                  Email
                </th>
                {/* <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                  Client Type
                </th> */}
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                  Products
                </th>
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                  Location
                </th>
                {/* <th scope="col" style={{ color: "black", fontWeight: "bold" }}>Business History</th> */}
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                  Ratings
                </th>
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                  Status
                </th>
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="12" className="text-center py-4">
                    Loading...
                  </td>
                </tr>
              ) : members.length > 0 ? (
                members.map((member, index) => (
                  <tr key={member._id || member.id}>
                    <td>{currentPage * rowsPerPage + index + 1}</td>
                    <td>{member.companyName}</td>
                    <td>
                      <div className="d-flex align-items-center">
                        <span className="text-md mb-0 fw-normal text-secondary-light">
                          {member.keyPerson}
                        </span>
                      </div>
                    </td>
                    <td>{member.mobile1}</td>
                    <td className="text-success-600 fw-bold">
                      {member.whatsappNumber || '-'}
                    </td>
                    <td>{member.email || '-'}</td>
                    {/* <td className=" fw-medium">{member.clientType || "-"}</td> */}
                    <td>
                      <div className=" text-secondary-light">
                        {(() => {
                          const productsList = Array.isArray(member.products)
                            ? member.products.map((p) =>
                              typeof p === "object" ? p.productName : p,
                            )
                            : [];

                          if (productsList.length === 0) return "-";
                          if (productsList.length === 1) return productsList[0];

                          return (
                            <div className="d-flex align-items-center gap-1">
                              <span>{productsList[0]}</span>
                              <span
                                className="badge bg-danger-focus text-danger-main cursor-help"
                                data-bs-toggle="tooltip"
                                data-bs-placement="top"
                                title={productsList.join(", ")}
                                style={{ fontSize: "10px" }}
                              >
                                +{productsList.length - 1}
                              </span>
                            </div>
                          );
                        })()}
                      </div>
                    </td>
                    <td>
                      <div className="text-secondary-light fw-medium">
                        {member.state || "-"}
                      </div>
                    </td>
                    {/* <td>
                      <Link 
                        to={`/clients/participation/${member._id || member.id}`}
                        className="btn btn-sm btn-danger px-12 py-4 radius-pill fw-bold"
                        style={{ fontSize: '12px' }}
                      >
                        {member.participatedExpos || 0} Expos
                      </Link>
                    </td> */}
                    <td>
                      <div className="d-flex align-items-center gap-1">
                        <Icon icon="solar:star-bold" className="text-warning" />
                        <span className="fw-bold">
                          {(() => {
                            const r = String(member.ratings || "");
                            const mapping = { "1": "VIP", "2": "ENQUIRY", "3": "OTHER EXHIBITOR", "4": "OWN EXHIBITOR" };
                            return mapping[r] || r || "OTHER EXHIBITOR";
                          })()}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span
                        className={`badge px-12 py-4 radius-pill fw-bold text-sm ${member.isActive ? 'bg-success-focus text-success-600' : 'bg-danger-focus text-danger-600'} ${isUpdating ? 'opacity-50' : ''}`}
                        style={{ minWidth: '80px' }}
                      >
                        {member.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-10">
                        <Link
                          to={`/clients/view/${member._id || member.id}`}
                          className="btn-view-custom fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          style={{
                            backgroundColor: "#eef2ff",
                            color: "#4338ca",
                            border: "1px solid #e0e7ff",
                          }}
                          title="View"
                        >
                          <Icon icon="lucide:eye" className="menu-icon" />
                        </Link>
                        {hasPermission("clients", "edit") && (
                          <Link
                            to={`/clients/edit/${member._id || member.id}`}
                            className="btn-edit-custom fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                            title="Edit"
                          >
                            <Icon icon="lucide:edit" className="menu-icon" />
                          </Link>
                        )}
                        {hasPermission("clients", "delete") && (
                          <button
                            type="button"
                            onClick={() => handleDeleteClick(member)}
                            className="bg-danger-focus text-danger-600 bg-hover-danger-200 w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0"
                            title="Delete"
                          >
                            <Icon icon="lucide:trash-2" className="text-xl" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="12" className="text-center py-4">
                    No members found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <TablePagination
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / rowsPerPage) || 1}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          totalRecords={totalRecords}
        />
      </div>

      <Modal show={showDeleteModal} onHide={handleCloseDelete} centered>
        <Modal.Body className="text-center p-5">
          <div className="d-flex justify-content-center mb-3">
            <div className="bg-danger-focus rounded-circle d-flex justify-content-center align-items-center w-64-px h-64-px">
              <Icon
                icon="lucide:trash-2"
                className="text-danger-600 text-xxl"
              />
            </div>
          </div>
          <h5 className="mb-3">Are you sure?</h5>
          <p className="text-secondary-light mb-4">
            Do you want to delete client "
            {memberToDelete?.companyName || memberToDelete?.fullName}"?
          </p>
          <div className="d-flex justify-content-center gap-3">
            <Button
              variant="outline-secondary"
              className="px-32"
              onClick={handleCloseDelete}
            >
              Cancel
            </Button>
            <Button
              className="px-32 border-0"
              onClick={confirmDelete}
              disabled={isUpdating}
              style={{ backgroundColor: "#ed1c24", color: "white" }}
            >
              {isUpdating ? "Deleting..." : "Confirm"}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Bulk Upload Modal */}
      <Modal
        show={showBulkUploadModal}
        onHide={() => {
          setShowBulkUploadModal(false);
          setSelectedFile(null);
        }}
        centered
        size="md"
      >
        <div className="modal-content radius-16 border-0 overflow-hidden">
          <div
            className="modal-header py-16 px-24 border-0 d-flex align-items-center justify-content-between"
            style={{
              background:
                "linear-gradient(135deg, #000000ff, #4f1818ff, #ff0000ff)",
            }}
          >
            <h6 className="text-white mb-0">Bulk Upload Clients</h6>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={() => {
                setShowBulkUploadModal(false);
                setSelectedFile(null);
              }}
            ></button>
          </div>
          <div className="modal-body p-32">
            <div className="text-center mb-24">
              <div className="bg-danger-focus rounded-circle d-flex justify-content-center align-items-center w-64-px h-64-px mx-auto mb-16">
                <Icon
                  icon="solar:upload-minimalistic-bold"
                  className="text-danger-600 text-xxl"
                />
              </div>
              <p className="text-secondary-light">
                Download the template, fill in your client data, and upload it
                back to the system.
              </p>
            </div>

            <div className="d-flex flex-column gap-4">
              {/* Download Section */}
              <div className="p-20 radius-12 border bg-neutral-50 d-flex align-items-center justify-content-between">
                <div>
                  <h6 className="text-sm mb-4 fw-bold">
                    Step 1: Download Template
                  </h6>
                  <p className="text-xs text-secondary-light mb-0">
                    Get the standard Excel/CSV format.
                  </p>
                </div>
                <button
                  className="btn btn-outline-danger btn-sm radius-8 d-flex align-items-center gap-2 fw-bold"
                  onClick={handleDownloadSample}
                >
                  <Icon icon="solar:download-bold" />
                  Download
                </button>
              </div>

              {/* Upload Section */}
              <div className="p-20 radius-12 border bg-neutral-50">
                <h6 className="text-sm mb-12 fw-bold">Step 2: Upload File</h6>
                <div
                  className="border-dashed radius-12 p-24 text-center cursor-pointer transition-all"
                  style={{
                    borderColor: "#ed1c24",
                    backgroundColor: "rgba(237, 28, 36, 0.02)",
                    border: "2px dashed #ed1c24",
                  }}
                  onClick={() =>
                    document.getElementById("bulkFileInput").click()
                  }
                >
                  <input
                    type="file"
                    id="bulkFileInput"
                    className="d-none"
                    accept=".csv, .xlsx, .xls"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        setSelectedFile(e.target.files[0]);
                      }
                      e.target.value = "";
                    }}
                  />
                  {selectedFile ? (
                    <div className="d-flex align-items-center justify-content-center gap-2">
                      <Icon
                        icon="solar:file-check-bold"
                        className="text-success text-2xl"
                      />
                      <span className="text-sm fw-bold text-dark">
                        {selectedFile.name}
                      </span>
                      <Icon
                        icon="line-md:close"
                        className="text-danger cursor-pointer ms-8"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFile(null);
                          const fileInput = document.getElementById("bulkFileInput");
                          if (fileInput) fileInput.value = "";
                        }}
                      />
                    </div>
                  ) : (
                    <>
                      <Icon
                        icon="solar:cloud-upload-bold"
                        className="text-danger-600 text-4xl mb-8"
                      />
                      <p className="text-sm text-secondary-light mb-0">
                        Click to browse or drag & drop files here
                      </p>
                      <p className="text-xs text-secondary-light opacity-75">
                        Supported formats: CSV, Excel
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer border-top p-16 bg-neutral-50 d-flex justify-content-center gap-3">
            <Button
              variant="outline-secondary"
              className="px-32 radius-8 fw-bold"
              onClick={() => {
                setShowBulkUploadModal(false);
                setSelectedFile(null);
              }}
            >
              Cancel
            </Button>
            <Button
              className="px-32 radius-8 border-0 fw-bold d-flex align-items-center gap-2"
              style={{ backgroundColor: "#ed1c24", color: "white" }}
              disabled={!selectedFile || isBulkUploading}
              onClick={handleImportClients}
            >
              {isBulkUploading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Uploading...
                </>
              ) : (
                <>
                  <Icon icon="solar:check-read-bold" />
                  Import Clients
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Error Download Modal */}
      <Modal
        show={showErrorDownloadModal}
        onHide={() => {
          setShowErrorDownloadModal(false);
          setErrorDownloadUrl("");
          setErrorCount(0);
        }}
        centered
        size="md"
      >
        <div className="modal-content radius-16 border-0 overflow-hidden">
          <div
            className="modal-header py-16 px-24 border-0 d-flex align-items-center justify-content-between"
            style={{
              background:
                "linear-gradient(135deg, #000000ff, #bb0007ff, #ff0000ff)",
            }}
          >
            <h6 className="text-white mb-0">Bulk Upload Errors</h6>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={() => {
                setShowErrorDownloadModal(false);
                setErrorDownloadUrl("");
                setErrorCount(0);
              }}
            ></button>
          </div>
          <div className="modal-body p-32 text-center">
            <div className="bg-danger-focus rounded-circle d-flex justify-content-center align-items-center w-64-px h-64-px mx-auto mb-24">
              <Icon
                icon="solar:danger-triangle-bold"
                className="text-danger-600 text-xxl"
              />
            </div>
            <h5 className="mb-12" style={{ color: "#ed1c24", fontWeight: "700" }}>
              Some Rows Have Errors!
            </h5>
            <p className="text-secondary-light mb-24">
              We found validation errors in <strong>{errorCount}</strong> row(s) of your uploaded file. 
              Please download the error report below to view the specific errors and correct them.
            </p>

            <div className="d-flex justify-content-center gap-3">
              <button
                type="button"
                className="btn btn-outline-secondary px-32 radius-8 fw-bold"
                onClick={() => {
                  setShowErrorDownloadModal(false);
                  setErrorDownloadUrl("");
                  setErrorCount(0);
                }}
              >
                Close
              </button>
              <button
                type="button"
                className="btn px-32 radius-8 fw-bold text-white border-0 d-flex align-items-center gap-2"
                style={{ backgroundColor: "#ed1c24" }}
                onClick={() => {
                  if (errorDownloadUrl) {
                    const link = document.createElement("a");
                    let downloadUrl = errorDownloadUrl;
                    if (!downloadUrl.startsWith("http")) {
                      const cleaned = downloadUrl.startsWith("/") ? downloadUrl : `/${downloadUrl}`;
                      downloadUrl = `${server}${cleaned}`;
                    }
                    link.href = downloadUrl;
                    link.setAttribute("download", `bulk_upload_errors_${Date.now()}.xlsx`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                  }
                }}
              >
                <Icon icon="solar:download-bold" />
                Download Error Report
              </button>
            </div>
          </div>
        </div>
      </Modal>

      <style>{`
        .theme-tooltip .tooltip-inner {
          background-color: #ed1c24 !important;
          color: white !important;
          font-weight: 600 !important;
          font-size: 12px !important;
          border-radius: 8px !important;
          padding: 8px 12px !important;
          box-shadow: 0 4px 15px rgba(237, 28, 36, 0.25) !important;
        }
        .theme-tooltip .tooltip-arrow::before {
          border-top-color: #ed1c24 !important;
          border-bottom-color: #ed1c24 !important;
        }
      `}</style>
    </div>
  );
};

export default ClientListLayer;
